import type { FetchResult, MutationFunctionOptions } from '@apollo/client';

import { asSettled, isRejected } from '../../sdk/promiseAllSettled';

import {
  CustomQuestionType,
  type DraftCustomQuestion,
} from './DraftCustomQuestion/DraftCustomQuestion';
import {
  customQuestionIdLocation,
  customQuestionToQuestionType,
} from './mapping';
import type {
  CreateCustomQuestion,
  CreateCustomQuestionVariables,
} from './operations';
import type { CustomQuestionResponse } from './types';

export const persistCustomQuestions =
  (
    createCustomQuestion: (
      options?: MutationFunctionOptions<
        CreateCustomQuestion,
        CreateCustomQuestionVariables
      >,
    ) => Promise<FetchResult<CreateCustomQuestion>>,
  ) =>
  async (
    draftCustomQuestions: DraftCustomQuestion[],
  ): Promise<CustomQuestionResponse[]> => {
    if (draftCustomQuestions.length === 0) {
      return [];
    }

    const requests = draftCustomQuestions
      .map(trimQuestionText)
      .filter(questionsWithText)
      .map((q) => {
        const request = createCustomQuestion({
          variables: {
            question: {
              languageId: 'en-AU',
              question: {
                text: q.text,
                questionType: customQuestionToQuestionType(q),
                options: q.options
                  .filter((o) => o.length > 0)
                  .map((text, order) => ({ text, order })),
              },
            },
          },
        });

        return asSettled(request, q.position);
      });

    const results = await Promise.all(requests);

    return results.map((res) => {
      if (isRejected(res)) {
        return {
          status: 'apiError',
          reason: res.reason.message,
          position: res.position,
        };
      }
      const result = res.value.data?.createCustomQuestion;
      if (!result) {
        return {
          status: 'apiError',
          reason: 'Empty response',
          position: res.position,
        };
      }
      if (result.__typename === 'CreateCustomQuestionErrorResult') {
        return {
          status: 'illegal',
          location: customQuestionIdLocation(res.position),
          reason: result.reason,
          position: res.position,
        };
      }
      if (result.__typename === 'CreateCustomQuestionResult') {
        return {
          status: 'complete',
          id: result.question.id,
          question: result.question,
          position: res.position,
        };
      }
      return {
        status: 'apiError',
        reason: 'Unknown typename',
        position: res.position,
      };
    });
  };

const questionsWithText = (question: DraftCustomQuestion) => {
  if (question.text.length > 0) {
    return true;
  }

  if (
    question.type === CustomQuestionType.MultipleChoice &&
    question.options.some((o) => o.length > 0)
  ) {
    return true;
  }

  return false;
};

const trimQuestionText = (
  question: DraftCustomQuestion,
): DraftCustomQuestion => ({
  ...question,
  text: question.text.trim(),
  options: question.options.map((o) => o.trim()),
});
